<script>
import { useAlert, useTrack } from 'dashboard/composables';
import BotMetrics from './components/BotMetrics.vue';
import ReportFilters from './components/ReportFilters.vue';
import { GROUP_BY_FILTER } from './constants';
import ReportContainer from './ReportContainer.vue';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ReportHeader from './components/ReportHeader.vue';
import ReportDownloadButton from './components/ReportDownloadButton.vue';
import ReportsAPI from 'dashboard/api/reports';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { downloadReportRows } from './helpers/csvExportHelper';

export default {
  name: 'BotReports',
  components: {
    BotMetrics,
    ReportHeader,
    ReportFilters,
    ReportContainer,
    ReportDownloadButton,
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      reportKeys: {
        BOT_HANDOFF_COUNT: 'bot_handoffs_count',
      },
      businessHours: false,
    };
  },
  computed: {
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
      };
    },
  },
  methods: {
    fetchAllData() {
      this.fetchBotSummary();
      this.fetchChartData();
    },
    fetchBotSummary() {
      try {
        this.$store.dispatch('fetchBotSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      Object.keys(this.reportKeys).forEach(async key => {
        try {
          await this.$store.dispatch('fetchAccountReport', {
            metric: this.reportKeys[key],
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const { from, to, groupBy, businessHours } = this;

      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
      };
    },
    onFilterChange({ from, to, groupBy, businessHours }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.businessHours = businessHours;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: { from, to, groupBy, businessHours },
        reportType: 'bots',
      });
    },
    async downloadBotReport(fileFormat) {
      if (!this.from || !this.to) return;
      try {
        const { data } = await ReportsAPI.getBotMetrics({
          from: this.from,
          to: this.to,
        });
        const rows = [
          [
            this.$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.LABEL'),
            this.$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL'),
            this.$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.LABEL'),
            this.$t('BOT_REPORTS.METRIC.HANDOFF_RATE.LABEL'),
          ],
          [
            data.conversation_count,
            data.message_count,
            `${data.resolution_rate}%`,
            `${data.handoff_rate}%`,
          ],
        ];
        const fileName = generateFileName({
          type: 'bot',
          to: this.to,
          businessHours: this.businessHours,
          fileFormat,
        });
        downloadReportRows(fileName, fileFormat, rows);
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('BOT_REPORTS.HEADER')">
    <ReportDownloadButton @download="downloadBotReport" />
  </ReportHeader>
  <div class="flex flex-col gap-4">
    <ReportFilters
      :show-entity-filter="false"
      show-group-by
      :show-business-hours="false"
      @filter-change="onFilterChange"
    />

    <BotMetrics :filters="requestPayload" />
    <ReportContainer
      account-summary-key="getBotSummary"
      summary-fetching-key="getBotSummaryFetchingStatus"
      :group-by="groupBy"
      :report-keys="reportKeys"
      :from="from"
      :to="to"
      :business-hours="businessHours"
    />
  </div>
</template>
